import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Loader2, Send, Trash2, X, Zap } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const EXAMPLES = ["Mostrami le iscrizioni degli ultimi 30 giorni", "Quante prenotazioni abbiamo ricevuto oggi?", "Preparami il riepilogo del fatturato mensile",
  "Controlla le verifiche professionali in sospeso", "Mostrami le automazioni che richiedono la mia approvazione", "Prepara un report sulle attività della clinica"];
const AGENT_NAMES = { booking: "Booking AI", registration: "Registration AI", clinic: "Clinic AI", director: "Director AI", email: "Email AI", cafe: "Café AI" };

function ActionConfirm({ k }) {
  const [state, setState] = useState("idle");
  const go = async () => {
    setState("busy");
    try {
      const { data } = await api.post(`/ai/agents/${k}/run`);
      if (data.skipped || data.error) toast.info(data.skipped || data.error);
      else toast.success(`${AGENT_NAMES[k]}: ${data.new_tasks} nuove attività`);
      setState("done");
    } catch (e) {
      toast.error(errMsg(e));
      setState("idle");
    }
  };
  return (
    <button data-testid={`sa-chat-action-${k}`} disabled={state !== "idle"} onClick={go} className="mt-2 flex items-center gap-1.5 rounded-lg border border-[#00F0FF]/40 px-3 py-1.5 text-xs font-semibold text-[#00F0FF] transition-colors hover:bg-[#00F0FF]/10 disabled:opacity-50">
      {state === "busy" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}{state === "done" ? "Eseguito" : `Conferma: avvia ${AGENT_NAMES[k]}`}
    </button>
  );
}

function Message({ m }) {
  return (
    <div className="space-y-2" data-testid="sa-chat-message">
      <p className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-[#00F0FF]/15 px-3 py-2 text-sm text-cyan-50">{m.q}</p>
      <div className="max-w-[92%] rounded-2xl rounded-tl-sm border border-slate-800 bg-slate-900/80 px-3 py-2 text-sm text-slate-200">
        <p className="whitespace-pre-wrap" data-testid="sa-chat-answer">{m.a.replace(/^#{1,6}\s*/gm, "").replace(/\*\*(.+?)\*\*/g, "$1").replace(/^\s*\*\s+/gm, "- ")}</p>
        {m.actions?.map((k) => <ActionConfirm key={k} k={k} />)}
        <p className="mt-1 font-mono text-[10px] text-slate-500">{fmtDateTime(m.at)}</p>
      </div>
    </div>
  );
}

export function AdminChat({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => {
    if (open) api.get("/superadmin/cc/chat/history").then(({ data }) => setItems(data.reverse())).catch(() => {});
  }, [open]);
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [items, busy]);
  const send = async (text) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/superadmin/cc/chat", { message: text });
      setItems((l) => [...l, data]);
    } catch (e) {
      toast.error(errMsg(e));
      setMsg(text);
    } finally {
      setBusy(false);
    }
  };
  const clear = () => api.delete("/superadmin/cc/chat/history").then(() => setItems([]));
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <aside data-testid="sa-chat-drawer" onClick={(e) => e.stopPropagation()} className="flex h-full w-full flex-col border-l border-[#00F0FF]/30 bg-[#0B0F19]/95 shadow-[-10px_0_40px_rgba(0,0,0,0.8)] backdrop-blur-2xl motion-safe:animate-in motion-safe:slide-in-from-right md:w-[460px]">
        <div className="flex items-center justify-between border-b border-slate-800 p-4">
          <p className="flex items-center gap-2 font-display font-bold text-white"><Bot className="h-5 w-5 text-[#00F0FF]" />Doctor Selvix AI · chat privata</p>
          <div className="flex gap-1">
            <button data-testid="sa-chat-clear" onClick={clear} title="Cancella cronologia" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 hover:bg-slate-800 hover:text-white"><Trash2 className="h-4 w-4" /></button>
            <button data-testid="sa-chat-close" onClick={onClose} aria-label="Chiudi" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 hover:bg-slate-800 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {!items.length && (
            <div className="space-y-2" data-testid="sa-chat-examples">
              <p className="text-xs text-slate-400">Risponde solo con i dati autorizzati della clinica. Le azioni richiedono sempre la tua conferma.</p>
              {EXAMPLES.map((e) => <button key={e} onClick={() => send(e)} className="block w-full rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2 text-left text-xs text-slate-300 transition-colors hover:border-[#00F0FF]/40 hover:text-white">"Doctor Selvix, {e.charAt(0).toLowerCase() + e.slice(1)}"</button>)}
            </div>
          )}
          {items.map((m) => <Message key={m.id} m={m} />)}
          {busy && <p className="flex items-center gap-2 text-xs text-[#00F0FF]" data-testid="sa-chat-thinking"><Loader2 className="h-3.5 w-3.5 animate-spin" />Doctor Selvix sta analizzando i dati…</p>}
          <div ref={end} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(msg); }} className="flex gap-2 border-t border-slate-800 p-3">
          <input data-testid="sa-chat-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Scrivi a Doctor Selvix…" className="h-11 flex-1 rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white placeholder:text-slate-500 focus:border-[#00F0FF]/60 focus:outline-none" />
          <button data-testid="sa-chat-send" disabled={busy || !msg.trim()} className="grid h-11 w-11 place-items-center rounded-xl bg-[#00F0FF] text-slate-950 disabled:opacity-40"><Send className="h-4 w-4" /></button>
        </form>
      </aside>
    </div>
  );
}
